// Admin-only (mars.mediacle@gmail.com) affiliate email export page.
// Preview N clean, de-duplicated affiliate emails from affiliate_clean,
// export the selection as a CSV for the external bulk emailer, and track
// which exported emails later signed up. The backend enforces the admin gate;
// non-admins hitting this page get a 403 state.
import React, { useCallback, useEffect, useState } from 'react';
import Sidebar from '../Sidebar';
import Header from '../Header';
import { Mail, Download, RefreshCw, Loader2, ShieldOff, CheckCircle2, Clock, Copy, Check, FileCode } from 'lucide-react';

const getApiUrl = () => process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

// Ready-to-send invite email body for the external bulk emailer.
// Suggested sender: MARS <no-reply@mediacle.com>
const INVITE_EMAIL_SUBJECT = "You're invited: connect with brands on MARS";
const buildInviteEmailHtml = (signupUrl, logoUrl) => `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>You're invited to MARS</title>
</head>
<body style="margin: 0; padding: 0; background-color: #f1f5f9; font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;">
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color: #f1f5f9; padding: 40px 20px;">
        <tr>
            <td align="center">
                <table role="presentation" width="560" cellpadding="0" cellspacing="0" style="max-width: 560px; width: 100%; background-color: #ffffff; border-radius: 8px; border: 1px solid #e2e8f0;">
                    <tr>
                        <td style="padding: 32px 40px 24px; text-align: center; border-bottom: 1px solid #f1f5f9;">
                            <img src="${logoUrl}" alt="MARS" style="height: 36px; display: inline-block;">
                        </td>
                    </tr>
                    <tr>
                        <td style="padding: 32px 40px 8px;">
                            <h1 style="margin: 0 0 20px; font-size: 20px; font-weight: 600; color: #1e293b; text-align: center;">You're invited to join MARS as an Affiliate Partner</h1>
                            <p style="margin: 0 0 16px; font-size: 15px; color: #334155; line-height: 1.6;">Hello,</p>
                            <p style="margin: 0 0 16px; font-size: 14px; color: #64748b; line-height: 1.7;">Your website has been identified as a valued affiliate by brands using <strong style="color:#334155;">MARS</strong>, our affiliate intelligence platform. Brands on MARS are looking to partner with sites like yours.</p>
                            <p style="margin: 0 0 8px; font-size: 14px; color: #64748b; line-height: 1.7;">Create your free affiliate account to:</p>
                        </td>
                    </tr>
                    <tr>
                        <td style="padding: 0 40px 24px;">
                            <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                                <tr><td style="padding: 6px 0; font-size: 14px; color: #334155; line-height: 1.6;">&#10003;&nbsp;&nbsp;Receive partnership offers directly from brands</td></tr>
                                <tr><td style="padding: 6px 0; font-size: 14px; color: #334155; line-height: 1.6;">&#10003;&nbsp;&nbsp;Chat with brand teams in one place &mdash; no email back-and-forth</td></tr>
                                <tr><td style="padding: 6px 0; font-size: 14px; color: #334155; line-height: 1.6;">&#10003;&nbsp;&nbsp;Free to join &mdash; takes less than 2 minutes</td></tr>
                            </table>
                        </td>
                    </tr>
                    <tr>
                        <td style="padding: 0 40px 28px; text-align: center;">
                            <table role="presentation" cellpadding="0" cellspacing="0" style="margin: 0 auto;">
                                <tr>
                                    <td style="background-color: #2563eb; border-radius: 6px;">
                                        <a href="${signupUrl}" style="display: inline-block; padding: 14px 44px; color: #ffffff; text-decoration: none; font-size: 14px; font-weight: 600;">Create Your Free Account</a>
                                    </td>
                                </tr>
                            </table>
                        </td>
                    </tr>
                    <tr>
                        <td style="padding: 0 40px 24px;">
                            <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color: #eff6ff; border-radius: 6px; border: 1px solid #dbeafe;">
                                <tr>
                                    <td style="padding: 12px 16px;">
                                        <p style="margin: 0; font-size: 13px; color: #1e40af; line-height: 1.5;">Sign up with this email address &mdash; it's how we recognize you as an invited affiliate.</p>
                                    </td>
                                </tr>
                            </table>
                        </td>
                    </tr>
                    <tr>
                        <td style="padding: 0 40px 28px;">
                            <p style="margin: 0; font-size: 12px; color: #94a3b8; line-height: 1.5;">If the button doesn't work, copy this link:</p>
                            <p style="margin: 4px 0 0; font-size: 12px; color: #2563eb; word-break: break-all; line-height: 1.5;">${signupUrl}</p>
                        </td>
                    </tr>
                    <tr>
                        <td style="padding: 20px 40px; border-top: 1px solid #f1f5f9; text-align: center;">
                            <p style="margin: 0 0 6px; font-size: 12px; color: #94a3b8; line-height: 1.5;">You received this email because your website is listed as an affiliate partner opportunity. If this isn't relevant to you, simply ignore this email or <a href="{{UNSUBSCRIBE_LINK}}" style="color: #94a3b8;">unsubscribe</a>.</p>
                            <p style="margin: 0; font-size: 11px; color: #cbd5e1;">&copy; ${new Date().getFullYear()} MARS by Mediacle &middot; mars@mediacle.com</p>
                        </td>
                    </tr>
                </table>
            </td>
        </tr>
    </table>
</body>
</html>`;

const AffiliateEmailExportLayout = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [openMenus, setOpenMenus] = useState({
    competitorResearch: false,
    messages: false,
    support: false,
    settings: false
  });
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentUser] = useState({ name: '', email: '', plan: '', avatar: '', initials: '' });

  const [accessDenied, setAccessDenied] = useState(false);
  const [previewLimit, setPreviewLimit] = useState(10);
  const [preview, setPreview] = useState([]);
  const [checked, setChecked] = useState({});
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportResult, setExportResult] = useState(null);
  const [exportsList, setExportsList] = useState([]);
  const [exportCounts, setExportCounts] = useState([]);
  const [loadingExports, setLoadingExports] = useState(true);
  const [copiedField, setCopiedField] = useState('');
  const [showEmailPreview, setShowEmailPreview] = useState(false);

  const toggleMenu = (menuKey) => {
    setOpenMenus(prev => ({ ...prev, [menuKey]: !prev[menuKey] }));
  };

  const loadExports = useCallback(async () => {
    setLoadingExports(true);
    try {
      const response = await fetch(`${getApiUrl()}/api/affiliate-signup/exports?limit=100`, {
        credentials: 'include'
      });
      if (response.status === 403) {
        setAccessDenied(true);
        return;
      }
      const data = await response.json();
      if (data.success) {
        setExportsList(data.rows);
        setExportCounts(data.counts);
      }
    } catch (_) { /* shown as empty list; user can refresh */ } finally {
      setLoadingExports(false);
    }
  }, []);

  useEffect(() => { loadExports(); }, [loadExports]);

  const loadPreview = async () => {
    setLoadingPreview(true);
    setExportResult(null);
    try {
      const response = await fetch(`${getApiUrl()}/api/affiliate-signup/preview?limit=${previewLimit}`, {
        credentials: 'include'
      });
      if (response.status === 403) {
        setAccessDenied(true);
        return;
      }
      const data = await response.json();
      if (data.success) {
        setPreview(data.emails);
        const allChecked = {};
        data.emails.forEach(e => { allChecked[e.email] = true; });
        setChecked(allChecked);
      }
    } catch (_) { /* keep previous preview; user can retry */ } finally {
      setLoadingPreview(false);
    }
  };

  const selectedEmails = preview.filter(e => checked[e.email]).map(e => e.email);

  const downloadCsv = (rows) => {
    const header = 'email,affiliate_url\n';
    const body = rows.map(r => `${r.email},${r.affiliate_url || ''}`).join('\n');
    const blob = new Blob([header + body], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `affiliate-emails-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExport = async () => {
    if (selectedEmails.length === 0) return;
    setExporting(true);
    setExportResult(null);
    try {
      const response = await fetch(`${getApiUrl()}/api/affiliate-signup/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ emails: selectedEmails })
      });
      if (response.status === 403) {
        setAccessDenied(true);
        return;
      }
      const data = await response.json();
      if (data.success) {
        setExportResult({ exported: data.exported.length, skipped: data.skipped.length });
        if (data.exported.length > 0) downloadCsv(data.exported);
        setPreview([]);
        setChecked({});
        loadExports();
      } else {
        setExportResult({ error: data.message || 'Export failed.' });
      }
    } catch (_) {
      setExportResult({ error: 'Export failed. Please try again.' });
    } finally {
      setExporting(false);
    }
  };

  const countFor = (status) => exportCounts.find(c => c.status === status)?.count || 0;

  const inviteEmailHtml = buildInviteEmailHtml(
    `${window.location.origin}/affiliate-signup`,
    `${window.location.origin}/MARS-black.png`
  );

  const copyToClipboard = async (field, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(''), 2000);
    } catch (_) { /* clipboard unavailable */ }
  };

  const content = accessDenied ? (
    <div className="flex items-center justify-center py-24">
      <div className="text-center max-w-sm">
        <ShieldOff className="w-10 h-10 text-red-400 mx-auto mb-4" />
        <h3 className="font-semibold text-gray-800 dark:text-white mb-2">Access denied</h3>
        <p className="text-sm text-gray-500">This page is restricted to the MARS administrator account.</p>
      </div>
    </div>
  ) : (
    <div className="space-y-6">
      {/* Preview / export card */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 p-6">
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Mail className="w-4 h-4 text-purple-600" /> Export affiliate emails
          </h2>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Fetch clean, de-duplicated affiliate contact emails (excluding already-exported and already-registered addresses),
          then download them as a CSV for your bulk emailer. Your invite email should link to{' '}
          <span className="font-mono text-xs bg-gray-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">{window.location.origin}/affiliate-signup</span>
        </p>

        <div className="flex items-center gap-3 mb-4">
          <input
            type="number"
            min={1}
            max={500}
            value={previewLimit}
            onChange={(e) => setPreviewLimit(Math.max(1, Math.min(500, parseInt(e.target.value, 10) || 1)))}
            className="w-24 px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-slate-800 dark:text-white"
          />
          <button
            onClick={loadPreview}
            disabled={loadingPreview}
            className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          >
            {loadingPreview ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Load preview
          </button>
          {preview.length > 0 && (
            <button
              onClick={handleExport}
              disabled={exporting || selectedEmails.length === 0}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
            >
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Export {selectedEmails.length} emails as CSV
            </button>
          )}
        </div>

        {exportResult && (
          <div className={`text-sm rounded-lg border p-3 mb-4 ${
            exportResult.error
              ? 'text-red-700 bg-red-50 border-red-200'
              : 'text-green-700 bg-green-50 border-green-200'
          }`}>
            {exportResult.error
              ? exportResult.error
              : `Exported ${exportResult.exported} emails (CSV downloaded).${exportResult.skipped ? ` ${exportResult.skipped} were skipped (already exported or registered).` : ''}`}
          </div>
        )}

        {preview.length > 0 && (
          <div className="overflow-x-auto border border-gray-200 dark:border-slate-700 rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-slate-800 text-left text-xs text-gray-500 uppercase">
                <tr>
                  <th className="px-4 py-2 w-10">
                    <input
                      type="checkbox"
                      checked={selectedEmails.length === preview.length}
                      onChange={(e) => {
                        const all = {};
                        preview.forEach(p => { all[p.email] = e.target.checked; });
                        setChecked(all);
                      }}
                    />
                  </th>
                  <th className="px-4 py-2">Email</th>
                  <th className="px-4 py-2">Affiliate site</th>
                  <th className="px-4 py-2 text-right">Organic traffic</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
                {preview.map((row) => (
                  <tr key={row.email} className="hover:bg-gray-50 dark:hover:bg-slate-800">
                    <td className="px-4 py-2">
                      <input
                        type="checkbox"
                        checked={!!checked[row.email]}
                        onChange={(e) => setChecked(prev => ({ ...prev, [row.email]: e.target.checked }))}
                      />
                    </td>
                    <td className="px-4 py-2 text-gray-900 dark:text-white">{row.email}</td>
                    <td className="px-4 py-2 text-gray-500">{row.affiliate_url}</td>
                    <td className="px-4 py-2 text-right text-gray-500">{(row.organic_traffic ?? 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Invite email template for the external bulk emailer */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 p-6">
        <h2 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-1">
          <FileCode className="w-4 h-4 text-blue-600" /> Invite email template
        </h2>
        <p className="text-sm text-gray-500 mb-4">
          Ready-to-send email body for your bulk emailer. Send it from{' '}
          <span className="font-mono text-xs bg-gray-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">no-reply@mediacle.com</span>{' '}
          and replace <span className="font-mono text-xs bg-gray-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">{'{{UNSUBSCRIBE_LINK}}'}</span>{' '}
          with your bulk emailer's unsubscribe tag.
        </p>

        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <button
            onClick={() => copyToClipboard('subject', INVITE_EMAIL_SUBJECT)}
            className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-gray-700 dark:text-slate-200 text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          >
            {copiedField === 'subject' ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
            Copy subject line
          </button>
          <button
            onClick={() => copyToClipboard('html', inviteEmailHtml)}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          >
            {copiedField === 'html' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            Copy email HTML
          </button>
          <button
            onClick={() => setShowEmailPreview(!showEmailPreview)}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            {showEmailPreview ? 'Hide preview' : 'Show preview'}
          </button>
        </div>

        <div className="text-xs text-gray-500 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-3 mb-4">
          <span className="font-medium text-gray-700 dark:text-slate-200">Subject:</span> {INVITE_EMAIL_SUBJECT}
        </div>

        {showEmailPreview && (
          <div className="border border-gray-200 dark:border-slate-700 rounded-lg overflow-hidden">
            <iframe
              title="Invite email preview"
              srcDoc={inviteEmailHtml}
              className="w-full bg-white"
              style={{ height: '640px', border: 'none' }}
            />
          </div>
        )}
      </div>

      {/* Export history / conversion */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-gray-900 dark:text-white">Export history</h2>
          <div className="flex items-center gap-4 text-sm">
            <span className="flex items-center gap-1.5 text-gray-500">
              <Clock className="w-4 h-4 text-amber-500" /> Exported: <strong>{countFor('exported')}</strong>
            </span>
            <span className="flex items-center gap-1.5 text-gray-500">
              <CheckCircle2 className="w-4 h-4 text-green-500" /> Signed up: <strong>{countFor('signed_up')}</strong>
            </span>
            <button onClick={loadExports} className="text-gray-400 hover:text-gray-600" title="Refresh">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {loadingExports ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-5 h-5 text-purple-500 animate-spin" />
          </div>
        ) : exportsList.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8">Nothing exported yet.</p>
        ) : (
          <div className="overflow-x-auto border border-gray-200 dark:border-slate-700 rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-slate-800 text-left text-xs text-gray-500 uppercase">
                <tr>
                  <th className="px-4 py-2">Email</th>
                  <th className="px-4 py-2">Affiliate site</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Exported</th>
                  <th className="px-4 py-2">Signed up</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
                {exportsList.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-slate-800">
                    <td className="px-4 py-2 text-gray-900 dark:text-white">{row.email}</td>
                    <td className="px-4 py-2 text-gray-500">{row.affiliate_url}</td>
                    <td className="px-4 py-2">
                      {row.status === 'signed_up' ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
                          <CheckCircle2 className="w-3 h-3" /> Signed up
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                          <Clock className="w-3 h-3" /> Exported
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-gray-500">
                      {row.exported_at ? new Date(row.exported_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-2 text-gray-500">
                      {row.signed_up_at
                        ? `${new Date(row.signed_up_at).toLocaleDateString()}${row.signed_up_first_name ? ` (${row.signed_up_first_name} ${row.signed_up_last_name || ''})` : ''}`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen transition-all duration-300 bg-gray-50 dark:bg-slate-950 text-sm">
      <Sidebar
        sidebarCollapsed={sidebarCollapsed}
        setSidebarCollapsed={setSidebarCollapsed}
        openMenus={openMenus}
        toggleMenu={toggleMenu}
      />
      <div className={`transition-all duration-300 ${sidebarCollapsed ? 'ml-16' : 'ml-64'}`}>
        <Header
          sidebarCollapsed={sidebarCollapsed}
          setSidebarCollapsed={setSidebarCollapsed}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          notifications={notifications}
          setNotifications={setNotifications}
          showNotifications={showNotifications}
          setShowNotifications={setShowNotifications}
          showProfileDropdown={showProfileDropdown}
          setShowProfileDropdown={setShowProfileDropdown}
          currentUser={currentUser}
        />
        <div className="p-6">
          <h1 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Affiliate Invites</h1>
          {content}
        </div>
      </div>
    </div>
  );
};

export default AffiliateEmailExportLayout;
